import { compareOrganisationNames } from "@/utils/organisationSort";
import { SidebarProvider } from "@/components/ui/sidebar";
import { AppSidebar } from "@/components/AppSidebar";
import { useCrmAccess } from "@/hooks/use-crm-access";
import { Navigate, useSearchParams } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Search, Filter, Users, Mail, LayoutGrid, List, Archive, UserCheck, ShieldCheck } from "lucide-react";
import { FixedPageHeader } from "@/components/ui/fixed-page-header";
import { PAGE_HEADERS } from "@/constants/page-headers";
import { useContacts } from "@/hooks/use-contacts";
import { useRoleBasedAccess } from "@/hooks/use-role-based-access";
import { useAuth } from "@/contexts/AuthContext";
import { ContactModal } from "@/components/crm/ContactModal";
import { ContactEditModal } from "@/components/crm/ContactEditModal";
import { ContactCard } from "@/components/crm/ContactCard";
import { ContactListView } from "@/components/crm/ContactListView";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { useState, useMemo } from "react";

const CrmContacts = () => {
  const { data: hasCrmAccess, isLoading: crmAccessLoading } = useCrmAccess();
  const { session } = useAuth();
  const { isPartner, partnerId, isSystemAdmin, isInternalStaff } = useRoleBasedAccess();
  const [searchParams] = useSearchParams();
  const initialOwnership = (() => {
    const v = searchParams.get("ownership");
    return v && ["pending", "approved", "rejected", "none", "all"].includes(v)
      ? v
      : "all";
  })();
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedContact, setSelectedContact] = useState<any>(null);
  const [relationshipFilter, setRelationshipFilter] = useState<string>("all");
  const [organizationFilter, setOrganizationFilter] = useState<string>("all");
  const [sortBy, setSortBy] = useState<string>("last_name_asc");
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [viewMode, setViewMode] = useState<"cards" | "list">("cards");
  const [showArchived, setShowArchived] = useState(false);
  const [onboardingFilter, setOnboardingFilter] = useState<string>("all");
  const [ownershipFilter, setOwnershipFilter] = useState<string>(initialOwnership);

  // Get all contacts (we'll filter them based on role)
  const allContacts = useContacts(showArchived);


  if (crmAccessLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-slate-600">Checking access...</div>
      </div>
    );
  }

  if (!hasCrmAccess) {
    return <Navigate to="/dashboard" replace />;
  }

  // Filter contacts based on user role
  const contactsData = isPartner && partnerId 
    ? (allContacts.data || []).filter(contact => contact.external_owner_id === partnerId)
    : (allContacts.data || []);

  // Extract unique organizations for filter dropdown
  const uniqueOrganizations = useMemo(() => {
    const orgs = contactsData
      .filter((c: any) => c.organizations?.name)
      .map((c: any) => ({ id: c.organization_id, name: c.organizations!.name }));
    // Deduplicate by organization ID
    return Array.from(new Map(orgs.map((o: any) => [o.id, o])).values())
      .sort((a: any, b: any) => compareOrganisationNames(a.name, b.name));
  }, [contactsData]);

  const isLoading = allContacts.isLoading;
  const error = allContacts.error;

  if (isLoading) {
    return (
      <SidebarProvider>
        <div className="flex min-h-screen w-full bg-background">
          <AppSidebar />
          <main className="flex-1">
            <div className="p-8">
              <div className="text-slate-600 text-center">Loading contacts...</div>
            </div>
          </main>
        </div>
      </SidebarProvider>
    );
  }

  if (error) {
    return (
      <SidebarProvider>
        <div className="flex min-h-screen w-full bg-background">
          <AppSidebar />
          <main className="flex-1">
            <div className="p-8">
              <Card className="bg-white border-red-200 rounded-xl shadow-sm">
                <CardContent className="p-8 text-center">
                  <div className="text-red-600 mb-4">
                    <Users className="h-12 w-12 mx-auto mb-4" />
                    <h3 className="text-lg font-semibold mb-2">Error Loading Contacts</h3>
                    <p className="text-sm mb-4">{error.message}</p>
                    <button 
                      onClick={() => window.location.reload()} 
                      className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
                    >
                      Try Again
                    </button>
                  </div>
                </CardContent>
              </Card>
            </div>
          </main>
        </div>
      </SidebarProvider>
    );
  }

  const handleEditContact = (contact: any) => {
    setSelectedContact(contact);
    setEditModalOpen(true);
  };

  // Filter and sort contacts
  const filteredAndSortedContacts = contactsData
    .filter((contact: any) => {
      // Search filter
      const searchMatch = searchTerm === "" || 
        `${contact.first_name || ''} ${contact.last_name || ''}`.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (contact.email || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (contact.contact_type || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (contact.organizations?.name || '').toLowerCase().includes(searchTerm.toLowerCase());
      
      // Relationship filter
      const relationshipMatch = relationshipFilter === "all" || 
        contact.relationship_status === relationshipFilter;
      
      // Organization filter
      const organizationMatch = organizationFilter === "all" || 
        (organizationFilter === "none" && !contact.organization_id) ||
        contact.organization_id === organizationFilter;
      
      // Onboarding status filter
      const onboardingMatch = onboardingFilter === "all" || 
        contact.onboarding_status === onboardingFilter;
      
      // Ownership approval filter
      const ownershipMatch = ownershipFilter === "all" || 
        (ownershipFilter === "pending" && contact.partner_ownership_status === "pending") ||
        (ownershipFilter === "approved" && contact.partner_ownership_status === "approved") ||
        (ownershipFilter === "rejected" && contact.partner_ownership_status === "rejected") ||
        (ownershipFilter === "none" && !contact.partner_ownership_status);
      
      return searchMatch && relationshipMatch && organizationMatch && onboardingMatch && ownershipMatch;
    })
    .sort((a: any, b: any) => {
      switch (sortBy) {
        case "last_name_asc":
          return (a.last_name || "").localeCompare(b.last_name || "");
        case "last_name_desc":
          return (b.last_name || "").localeCompare(a.last_name || "");
        case "created_at_desc":
          return new Date(b.created_at || 0).getTime() - new Date(a.created_at || 0).getTime();
        case "updated_at_desc":
          return new Date(b.updated_at || 0).getTime() - new Date(a.updated_at || 0).getTime();
        default:
          return 0;
      }
    });

  return (
    <SidebarProvider>
      <div className="flex min-h-screen w-full bg-background">
        <AppSidebar />
        <main className="flex-1">
          <FixedPageHeader {...PAGE_HEADERS.crmContacts} actions={<ContactModal />} />
          <div className="px-4 md:px-8 py-6 md:py-8 space-y-8" style={{ paddingTop: "8.5rem" }}>
            
            <Alert>
              <Mail className="h-4 w-4" />
              <AlertTitle>Inviting New Users</AlertTitle>
              <AlertDescription>
                Create a contact here first, then click the "Send Invitation" button on their contact card to send them a branded invitation email.
              </AlertDescription>
            </Alert>

            {/* Summary Card */}
            <div className="grid grid-cols-1 md:grid-cols-1 gap-6 max-w-md">
              <Card className="bg-gradient-to-br from-white to-slate-50 border-slate-200 rounded-xl shadow-sm">
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-sm font-semibold text-slate-700">Total Contacts</CardTitle>
                    <div className="bg-slate-100 p-2 rounded-lg">
                      <Users className="h-5 w-5 text-slate-600" />
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-bold text-slate-800">{contactsData.length}</div>
                </CardContent>
              </Card>
            </div>

            {/* Search and Filters */}
            <div className="flex items-center gap-4 flex-wrap">
              <div className="relative flex-1 max-w-md">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-500 h-4 w-4" />
                <Input
                  placeholder="Search contacts..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 border-slate-300 focus:border-teal-500 focus:ring-teal-500 rounded-lg bg-white"
                />
              </div>
              
              <div className="flex items-center gap-2">
                <Filter className="h-4 w-4 text-slate-500" />
                <Select value={relationshipFilter} onValueChange={setRelationshipFilter}>
                  <SelectTrigger className="w-[140px] bg-white border-slate-300">
                    <SelectValue placeholder="Status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Status</SelectItem>
                    <SelectItem value="Prospect">Prospects</SelectItem>
                    <SelectItem value="Customer">Customers</SelectItem>
                  </SelectContent>
                </Select>

                <Select value={onboardingFilter} onValueChange={setOnboardingFilter}>
                  <SelectTrigger className="w-[160px] bg-white border-slate-300">
                    <UserCheck className="h-4 w-4 mr-2 text-slate-500" />
                    <SelectValue placeholder="Onboarding" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Users</SelectItem>
                    <SelectItem value="onboarded">Onboarded</SelectItem>
                    <SelectItem value="invited_pending">Invited (Pending)</SelectItem>
                    <SelectItem value="invited_expired">Invited (Expired)</SelectItem>
                    <SelectItem value="not_invited">Not Invited</SelectItem>
                  </SelectContent>
                </Select>

                {/* Ownership approval filter - only for staff/superusers */}
                {(isSystemAdmin || isInternalStaff) && (
                  <Select value={ownershipFilter} onValueChange={setOwnershipFilter}>
                    <SelectTrigger className="w-[180px] bg-white border-slate-300">
                      <ShieldCheck className="h-4 w-4 mr-2 text-slate-500" />
                      <SelectValue placeholder="Ownership" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Ownership</SelectItem>
                      <SelectItem value="pending">Pending Approval</SelectItem>
                      <SelectItem value="approved">Approved</SelectItem>
                      <SelectItem value="rejected">Rejected</SelectItem>
                      <SelectItem value="none">No Partner Claim</SelectItem>
                    </SelectContent>
                  </Select>
                )}
              </div>

              <Select value={organizationFilter} onValueChange={setOrganizationFilter}>
                <SelectTrigger className="w-[180px] bg-white border-slate-300">
                  <SelectValue placeholder="Organization" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Organizations</SelectItem>
                  <SelectItem value="none">No Organization</SelectItem>
                  {uniqueOrganizations.map((org: any) => (
                    <SelectItem key={org.id} value={org.id}>{org.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              
              <Select value={sortBy} onValueChange={setSortBy}>
                <SelectTrigger className="w-[140px] bg-white border-slate-300">
                  <SelectValue placeholder="Sort by" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="last_name_asc">Last Name A-Z</SelectItem>
                  <SelectItem value="last_name_desc">Last Name Z-A</SelectItem>
                  <SelectItem value="created_at_desc">Recently Added</SelectItem>
                  <SelectItem value="updated_at_desc">Recently Updated</SelectItem>
                </SelectContent>
              </Select>

              <ToggleGroup 
                type="single" 
                value={viewMode} 
                onValueChange={(v) => v && setViewMode(v as "cards" | "list")}
                className="border border-slate-300 rounded-md"
              >
                <ToggleGroupItem value="cards" aria-label="Card view" className="px-3">
                  <LayoutGrid className="h-4 w-4" />
                </ToggleGroupItem>
                <ToggleGroupItem value="list" aria-label="List view" className="px-3">
                  <List className="h-4 w-4" />
                </ToggleGroupItem>
              </ToggleGroup>

              {/* Show Archived Toggle */}
              <div className="flex items-center gap-2 ml-2 pl-2 border-l border-slate-300">
                <Switch
                  id="show-archived"
                  checked={showArchived}
                  onCheckedChange={setShowArchived}
                />
                <Label htmlFor="show-archived" className="text-sm text-slate-600 flex items-center gap-1 cursor-pointer">
                  <Archive className="h-4 w-4" />
                  Show Archived
                </Label>
              </div>
            </div>

            {/* Contacts Grid or List */}
            {viewMode === "cards" ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {filteredAndSortedContacts.map((contact: any) => (
                  <ContactCard
                    key={contact.id}
                    contact={contact}
                    onEdit={handleEditContact}
                  />
                ))}
              </div>
            ) : (
              <ContactListView 
                contacts={filteredAndSortedContacts} 
                onEdit={handleEditContact} 
              />
            )}

            {/* No results message */}
            {filteredAndSortedContacts.length === 0 && (
              <Card className="bg-white border-slate-200 rounded-xl shadow-sm">
                <CardContent className="p-8 text-center">
                  <Users className="h-12 w-12 text-slate-400 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-slate-800 mb-2">No Contacts Found</h3>
                  <p className="text-slate-600 mb-4">
                    {searchTerm ? "No contacts match your search criteria." : "Get started by adding your first contact."}
                  </p>
                  {!searchTerm && <ContactModal />}
                </CardContent>
              </Card>
            )}

            {/* Contact Edit Modal */}
            {selectedContact && (
              <ContactEditModal
                contact={selectedContact}
                isOpen={editModalOpen}
                onClose={() => {
                  setEditModalOpen(false);
                  setSelectedContact(null);
                }}
                onSuccess={() => {
                  // Refresh contacts will happen automatically via react-query
                }}
              />
            )}
          </div>
        </main>
      </div>
    </SidebarProvider>
  );
};

export default CrmContacts;